/**
 * ToggleSwitchHandler — 비동기 toggle-switch 처리.
 *
 * admin.components.toggle-switch (PHP) 가 렌더한 `.admin-toggle-switch-form` 폼들을
 * FormWrapper/Form.ts 로 위임한다. 기존 page controller import 안정성을 위한 shim.
 *
 * 사용:
 *   ToggleSwitchHandler.init(root);          // root 내 모든 .admin-toggle-switch-form 적용
 *   ToggleSwitchHandler.init(root, options); // 커스텀 콜백 포함
 *
 * 카탈로그: docs/03-constants/components.md (ToggleSwitchHandler)
 * 규칙: 상태변경/CRUD 작업은 Toast 패턴 사용 — docs/01-rules/must-check.md E-2
 */

import { bindForm } from "../../core/FormWrapper";

export interface ToggleSwitchHandlerOptions {
  /**
   * 토글 성공 후 콜백. DOM 업데이트(클래스·hint) 뒤에 호출된다.
   * result.enabled = 새 상태.
   */
  onSuccess?: (form: HTMLFormElement, result: ToggleResult) => void;
  /**
   * 오류 시 Toast.error 외에 추가 처리.
   */
  onError?: (form: HTMLFormElement, error: unknown) => void;
  /**
   * true 면 오류 시 Toast.error 대신 Modal.alert (치명적 오류에 사용).
   */
  criticalError?: boolean;
}

export interface ToggleResult {
  result: "success" | "fail";
  msg: string;
  enabled: boolean;
}

const HINT_ON = "켜짐";
const HINT_OFF = "꺼짐";

export const ToggleSwitchHandler = {
  /**
   * root 내 모든 .admin-toggle-switch-form 에 비동기 처리 바인딩.
   * 이미 data-ts-toggle-bound 가 있는 폼은 건너뜀.
   */
  init(root: HTMLElement | Document = document, options: ToggleSwitchHandlerOptions = {}): void {
    root.querySelectorAll<HTMLFormElement>(".admin-toggle-switch-form").forEach((form) => {
      form.dataset.adminFormKind = "toggle-switch";
      bindForm(form);
      if (options.onSuccess) {
        form.addEventListener("admin:form:success", (event) => {
          const detail = (event as CustomEvent).detail as { normalized?: { payload?: Record<string, unknown> } };
          const payload = detail.normalized?.payload ?? {};
          options.onSuccess?.(form, {
            result: "success",
            msg: String(payload.message ?? ""),
            enabled: Boolean(payload.enabled),
          });
        });
      }
    });
  },

  _bindForm(form: HTMLFormElement, options: ToggleSwitchHandlerOptions): void {
    form.dataset.adminFormKind = "toggle-switch";
    bindForm(form);
  },

  _applyState(
    form: HTMLFormElement,
    btn: HTMLButtonElement | null,
    hint: HTMLElement | null,
    enabled: boolean,
  ): void {
    // 버튼 클래스 + aria
    if (btn) {
      btn.classList.toggle("is-on", enabled);
      btn.classList.toggle("is-off", !enabled);
      btn.setAttribute("aria-pressed", enabled ? "true" : "false");
      btn.disabled = false;
    }
    // hint 텍스트 (현재 상태 표시)
    if (hint) hint.textContent = enabled ? HINT_ON : HINT_OFF;
    // form data-confirm 갱신 (끄는 방향일 때만 표시)
    if (enabled) {
      const label = form.closest("[data-feature-key]")?.querySelector(".admin-features__item-label")?.textContent ?? "이 기능";
      form.dataset.confirm = `'${label}' 을 끄면 관련 라우트가 404 가 됩니다. 계속할까요?`;
    } else {
      delete form.dataset.confirm;
    }
    // 부모 li 상태 클래스 업데이트 (선 색 등)
    const li = form.closest<HTMLElement>("[data-feature-key]");
    if (li) {
      li.classList.toggle("is-on", enabled);
      li.classList.toggle("is-off", !enabled);
    }
  },

  _showError(form: HTMLFormElement, msg: string, options: ToggleSwitchHandlerOptions): void {
    options.onError?.(form, new Error(msg));
  },
};
